import React, { useCallback, useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Network, FileDown, Loader2, Sparkles, AlertTriangle, ShieldCheck } from "lucide-react";

const PRI_CLS = {
  P1: "border-red-300 bg-red-50 text-red-700",
  P2: "border-amber-300 bg-amber-50 text-amber-700",
  P3: "border-zinc-300 bg-zinc-50 ek-text-muted",
};

export default function DeliveryTomPanel() {
  const [meta, setMeta] = useState(null);
  const [clients, setClients] = useState([]);
  const [engagementId, setEngagementId] = useState("");
  const [ctx, setCtx] = useState(null);
  const [derived, setDerived] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [view, setView] = useState("advisor"); // advisor | portfolio
  const [portfolio, setPortfolio] = useState(null);

  useEffect(() => {
    if (view === "portfolio" && !portfolio) {
      api.get("/admin/delivery-tom/portfolio")
        .then(({ data }) => setPortfolio(data))
        .catch(() => toast.error("Could not load the TOM portfolio"));
    }
  }, [view, portfolio]);

  useEffect(() => {
    api.get("/me").then(({ data }) => setIsOwner(data?.platform_role === "founder")).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/admin/delivery-tom/meta").then(({ data }) => {
      setMeta(data);
      setCtx(data.default_context);
    }).catch(() => toast.error("Could not load TOM metadata"));
    api.get("/admin/enterprise360/clients").then(({ data }) => {
      const list = (data.clients || data || []).map((c) => ({
        id: c.id, name: c.profile?.client_name || c.id,
      }));
      setClients(list);
    }).catch(() => {});
  }, []);

  const resolve = useCallback(async (context, engId) => {
    setLoading(true);
    try {
      const { data } = await api.post("/admin/delivery-tom/resolve", { context, engagement_id: engId || null });
      setResult(data);
    } catch (e) { toast.error("Could not resolve operating model"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (ctx) resolve(ctx, engagementId); /* eslint-disable-next-line */ }, [ctx]);

  const toggleVerify = async (r) => {
    setBusyId(r.id);
    try {
      await api.post(`/admin/delivery-tom/items/${r.id}/verify`, { verified: r.unverified });
      toast.success(r.unverified ? "Marked SME-verified" : "Verification removed");
      resolve(ctx, engagementId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update verification");
    } finally { setBusyId(null); }
  };

  const onDerive = async (id) => {
    setEngagementId(id);
    if (!id) { setDerived({}); return; }
    try {
      const { data } = await api.get(`/admin/delivery-tom/engagements/${id}/context`);
      setDerived(data.derived || {});
      setCtx((prev) => ({ ...prev, ...data.context }));
      toast.success("Context derived from engagement — adjust below as needed");
    } catch (e) { toast.error("Could not derive context"); }
  };

  const exportPdf = async () => {
    setExporting(true);
    try {
      const res = await api.post("/admin/delivery-tom/export.pdf",
        { context: ctx, engagement_id: engagementId || null }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = "delivery-tom-sow-starter.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("TOM SOW starter exported");
    } catch (e) { toast.error("Could not export PDF"); }
    finally { setExporting(false); }
  };

  if (!meta || !ctx) {
    return <div className="ek-admin flex items-center gap-2 ek-text-muted text-sm py-10"><Loader2 size={16} className="animate-spin" /> Loading…</div>;
  }

  const grouped = {};
  (result?.backlog || []).forEach((r) => { (grouped[r.category_label] = grouped[r.category_label] || []).push(r); });

  return (
    <div className="ek-admin space-y-6" data-testid="delivery-tom-panel">
      <div>
        <h2 className="ek-heading text-xl flex items-center gap-2"><Network size={20} /> IT Delivery / DevOps operating model</h2>
        <p className="ek-text-muted text-[13px] mt-1 max-w-2xl">
          Context-sensitive target-operating-model advice — set the org context (or derive it from an engagement) and get a
          tailored archetype plus a ranked org-design / automation / DevOps / AI backlog. Deterministic & reproducible.
        </p>
      </div>

      <div className="flex gap-2" data-testid="tom-view-toggle">
        {[{ k: "advisor", l: "Advisor" }, { k: "portfolio", l: "Portfolio" }].map(({ k, l }) => (
          <button key={k} onClick={() => setView(k)} data-testid={`tom-view-${k}`}
                  className={`px-3 py-1.5 text-[12px] tracking-[0.12em] uppercase border rounded ${view === k ? "border-[#7737F6] text-[#7737F6] bg-[#7737F6]/5" : "ek-line ek-text-muted hover:bg-zinc-50"}`}>{l}</button>
        ))}
      </div>

      {view === "portfolio" && (
        !portfolio ? (
          <div className="flex items-center gap-2 ek-text-muted text-sm py-6"><Loader2 size={16} className="animate-spin" /> Loading portfolio…</div>
        ) : (
          <div className="space-y-5" data-testid="tom-portfolio">
            <p className="ek-text-muted text-[13px] max-w-2xl">Which operating-model archetypes recur across your {portfolio.count} engagement(s), the delivery-maturity spread, and SME verification coverage — the deterministic proof-flywheel view.</p>
            <div>
              <h3 className="ek-text-soft text-[12px] uppercase tracking-[0.14em] mb-2">Archetype distribution</h3>
              <div className="space-y-2">
                {portfolio.distribution.map((d) => (
                  <div key={d.archetype} data-testid={`tom-dist-${d.archetype}`} className="flex items-center gap-3">
                    <div className="w-52 shrink-0 text-[12.5px] ek-text-soft flex items-center gap-1.5">
                      {d.label}{d.is_top && <span className="text-[10px] text-[#7737F6] border border-[#7737F6]/40 rounded px-1">most common</span>}
                    </div>
                    <div className="flex-1 h-6 rounded ek-surface border ek-line overflow-hidden">
                      <div className="h-6 bg-[#7737F6]/80 rounded-r flex items-center px-2" style={{ width: `${Math.max(d.pct, d.count ? 8 : 0)}%` }}>
                        <span className="text-[11px] font-semibold text-white tabular-nums">{d.count}</span>
                      </div>
                    </div>
                    <div className="w-12 shrink-0 text-right text-[11px] ek-text-muted tabular-nums">{d.pct}%</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex flex-wrap gap-2" data-testid="tom-maturity-dist">
                <span className="text-[12px] ek-text-soft">Delivery maturity:</span>
                {Object.entries(portfolio.maturity_distribution).map(([m, n]) => (
                  <span key={m} className="inline-flex items-center gap-1 text-[12px] ek-surface border ek-line rounded-full px-3 py-1 capitalize">{m} <b className="tabular-nums">{n}</b></span>
                ))}
              </div>
              <div className="text-[12px] ek-text-soft" data-testid="tom-portfolio-verification">
                SME-verified items: <b className="text-emerald-700">{portfolio.verification.tom_verified}/{portfolio.verification.tom_total}</b> ({portfolio.verification.pct}%)
              </div>
            </div>
            <div className="border ek-line rounded-lg overflow-hidden">
              <table className="w-full text-[13px]">
                <thead><tr className="ek-surface-2 text-left ek-text-soft">
                  <th className="p-2.5 font-medium">Engagement</th><th className="p-2.5 font-medium">Archetype</th>
                  <th className="p-2.5 font-medium">Maturity</th><th className="p-2.5 font-medium">Drivers</th></tr></thead>
                <tbody>
                  {portfolio.engagements.map((e) => (
                    <tr key={e.id} className="border-t ek-line" data-testid={`tom-portfolio-eng-${e.id}`}>
                      <td className="p-2.5 ek-text">{e.client_name}{e.is_demo && <span className="ml-1.5 text-[10px] ek-text-muted border ek-line rounded px-1">demo</span>}</td>
                      <td className="p-2.5 ek-text-soft">{e.archetype_label}</td>
                      <td className="p-2.5 ek-text-muted capitalize">{e.delivery_maturity}</td>
                      <td className="p-2.5 ek-text-muted text-[11.5px]">{(e.drivers || []).join(" · ")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      )}

      {view === "advisor" && (<>
      <div className="border ek-line rounded-xl p-4 ek-surface space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h3 className="ek-text-soft text-[12px] uppercase tracking-[0.14em]">Organisation context</h3>
          <label className="text-[12px] ek-text-soft flex items-center gap-2">
            Derive from engagement
            <select value={engagementId} onChange={(e) => onDerive(e.target.value)} data-testid="tom-engagement-select"
                    className="border ek-line rounded-lg px-2 py-1.5 text-sm ek-surface">
              <option value="">— none —</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {Object.entries(meta.dimensions).map(([dim, d]) => (
            <label key={dim} className="text-[12px] ek-text-soft block">
              {d.label}{derived[dim] && <span className="ml-1 text-[10px] text-[#7737F6]">• derived</span>}
              <select value={ctx[dim]} data-testid={`tom-dim-${dim}`}
                      onChange={(e) => setCtx((p) => ({ ...p, [dim]: e.target.value }))}
                      className="mt-1 border ek-line rounded-lg px-3 py-2 text-sm ek-surface w-full capitalize">
                {d.options.map((o) => <option key={o} value={o}>{o.replace(/_/g, " ")}</option>)}
              </select>
            </label>
          ))}
        </div>
      </div>

      {/* Result */}
      {loading ? (
        <div className="flex items-center gap-2 ek-text-muted text-sm py-6"><Loader2 size={16} className="animate-spin" /> Resolving…</div>
      ) : result ? (
        <>
          <div className="rounded-xl border border-violet-300 bg-violet-50 p-4" data-testid="tom-archetype">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="text-[11px] uppercase tracking-[0.14em] text-[#7737F6] flex items-center gap-1.5"><Sparkles size={13} /> Recommended archetype</div>
                <div className="text-lg font-semibold ek-text mt-0.5" data-testid="tom-archetype-label">{result.archetype_detail.label}</div>
                <p className="text-[12.5px] ek-text-soft mt-1 max-w-2xl">{result.archetype_detail.summary}</p>
                <p className="text-[12px] ek-text-muted mt-1"><strong>Topology:</strong> {result.archetype_detail.topology}</p>
                {result.drivers?.length ? <p className="text-[11px] ek-text-muted mt-1">Drivers: {result.drivers.join(" · ")}</p> : null}
              </div>
              <button onClick={exportPdf} disabled={exporting || !result.count} data-testid="tom-export-pdf"
                      className="inline-flex items-center gap-1.5 px-3 py-2 text-[12px] border ek-line rounded-lg ek-text-soft hover:bg-white disabled:opacity-40 shrink-0">
                <FileDown size={14} /> {exporting ? "Exporting…" : "TOM SOW PDF"}
              </button>
            </div>
            <div className="text-[12px] ek-text-muted mt-2">
              Backlog: <span className="text-red-700 font-semibold">{result.counts.P1} P1</span> · <span className="text-amber-700 font-semibold">{result.counts.P2} P2</span> · {result.counts.P3} P3
            </div>
          </div>

          <div className="rounded-lg border border-amber-300 bg-amber-50 text-amber-800 px-3 py-2 text-[11.5px] flex items-start gap-2" data-testid="tom-disclaimer">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" /> {result.disclaimer}
          </div>

          {Object.entries(grouped).map(([cat, items]) => (
            <div key={cat}>
              <h3 className="ek-text-soft text-[12px] uppercase tracking-[0.14em] mb-2">{cat}</h3>
              <div className="space-y-2">
                {items.map((r) => (
                  <div key={r.id} data-testid={`tom-item-${r.id}`} className="border ek-line rounded-lg px-3 py-2 ek-surface">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] font-semibold border rounded px-1.5 py-0.5 ${PRI_CLS[r.priority]}`}>{r.priority}</span>
                      <span className="text-[13px] font-semibold ek-text">{r.title}</span>
                      <span className="text-[10px] ek-text-muted border ek-line rounded px-1.5 py-0.5">{r.effort} effort</span>
                      {r.unverified
                        ? <span className="text-[10px] text-amber-700 flex items-center gap-0.5" data-testid={`tom-item-unverified-${r.id}`}><AlertTriangle size={9} /> unverified</span>
                        : <span className="text-[10px] text-emerald-700 flex items-center gap-0.5" data-testid={`tom-item-verified-${r.id}`}><ShieldCheck size={9} /> verified</span>}
                    </div>
                    <p className="text-[12px] ek-text-muted mt-1">{r.capability}</p>
                    <div className="mt-2">
                      {r.unverified ? (
                        <button onClick={() => toggleVerify(r)} disabled={!isOwner || busyId === r.id}
                                data-testid={`tom-item-verify-${r.id}`}
                                title={isOwner ? "" : "Only an accredited owner can SME-verify"}
                                className="text-[11px] px-2 py-1 border border-emerald-300 text-emerald-700 rounded hover:bg-emerald-50 disabled:opacity-40">
                          {busyId === r.id ? "Saving…" : "Mark SME-verified"}
                        </button>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="text-[11px] ek-text-muted">Verified{r.verified_by ? ` by ${r.verified_by}` : ""}</span>
                          <button onClick={() => toggleVerify(r)} disabled={!isOwner || busyId === r.id}
                                  data-testid={`tom-item-unverify-${r.id}`}
                                  className="text-[11px] px-2 py-1 border ek-line rounded hover:bg-zinc-50 disabled:opacity-40">
                            {busyId === r.id ? "Saving…" : "Remove verification"}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </>
      ) : null}
      </>)}
    </div>
  );
}
